import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useSelector } from 'react-redux';

import CartProduct from '../UI/CartProduct';

import '../../styles/cart-modal.css'
import { Link } from 'react-router-dom';

const CartModal = ({ isOpen, toggle }) => {
    const cartItems = useSelector(state => state.cart.cartItems);
    const totalAmount = Number(useSelector((state) => state.cart.totalAmount));
    const [isProcessing, setIsProcessing] = useState(false);
    const [applePayAvailable, setApplePayAvailable] = useState(false);

    // Check Apple Pay availability after component mounts
    useEffect(() => {
        const checkApplePay = () => {
            if (typeof window !== 'undefined' && window.ApplePaySession) {
                setApplePayAvailable(window.ApplePaySession.canMakePayments());
            }
        };
        
        checkApplePay();
    }, []);

    // Apple Pay icon SVG
    const ApplePayIcon = ({ className = "" }) => (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
    );

    const handleApplePay = async () => {
        if (!applePayAvailable) {
            alert('Apple Pay is not available in this browser. Please use Safari on an Apple device.');
            return;
        }

        setIsProcessing(true);

        try {
            const paymentRequest = {
                countryCode: 'US',
                currencyCode: 'USD',
                supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
                merchantCapabilities: ['supports3DS'],
                total: {
                    label: 'Lordvision Garments',
                    amount: totalAmount.toFixed(2)
                }
            };

            const session = new window.ApplePaySession(3, paymentRequest);

            session.onvalidatemerchant = async (event) => {
                try {
                    const merchantSession = await validateMerchant(event.validationURL);
                    session.completeMerchantValidation(merchantSession);
                } catch (error) {
                    session.abort();
                    alert('Payment setup failed. Please try again.');
                    setIsProcessing(false);
                }
            };

            session.onpaymentauthorized = async (event) => {
                try {
                    const paymentSuccess = await processPayment(event.payment);
                    
                    if (paymentSuccess) {
                        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
                        toggle();
                        window.location.href = '/order-success';
                    } else {
                        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
                        alert('Payment failed. Please try again.');
                    }
                } catch (error) {
                    session.completePayment(window.ApplePaySession.STATUS_FAILURE);
                    alert('Payment processing failed. Please try again.');
                } finally {
                    setIsProcessing(false);
                }
            };

            session.oncancel = () => {
                setIsProcessing(false);
            };

            session.begin();

        } catch (error) {
            alert('Apple Pay failed to initialize. Please try again.');
            setIsProcessing(false);
        }
    };

    // Mock functions (same as before)
    const validateMerchant = async (validationURL) => {
        try {
            // Simulate API call to your backend
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Return a mock merchant session
            return {
                merchantSession: {
                    merchantIdentifier: "merchant.com.yourdomain",
                    displayName: "Lordvision Garments",
                    initiative: "web",
                    initiativeContext: window.location.hostname
                }
            };
        } catch (error) {
            console.error('Merchant validation failed:', error);
            throw new Error('Merchant validation failed');
        }
    };

    const processPayment = async (payment) => {
        try {
            // Simulate API call to process payment
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // For testing, always return success
            // In production, you would call your actual payment processor API
            console.log('Processing payment:', payment);
            return true;
            
        } catch (error) {
            console.error('Payment processing failed:', error);
            return false;
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} className='cart-modal'>
            <ModalHeader className='cart-header' toggle={toggle}>Your Cart</ModalHeader>
            {
                cartItems.length === 0 ? (
                    <span className='modal-message'> No item added to the cart. </span>
                ) : (
                    <>
                        <ModalBody>
                            <CartProduct cartItems={cartItems} />
                        </ModalBody>
                        
                        <ModalFooter>
                            <div className='subtotal'>
                                <p>Subtotal</p>
                                <p className='fw-bold'>$ {totalAmount.toFixed(2)} USD</p>
                            </div>

                            <Link 
                                to='/checkout' 
                                className='modal-btn btn btn-secondary' 
                                onClick={toggle}
                            >
                                Continue to Checkout
                            </Link>

                            {/* Only show Apple Pay button if available */}
                            {true && (
                                <button 
                                    className='apple-pay-btn'
                                    onClick={handleApplePay}
                                    disabled={isProcessing || totalAmount <= 0}
                                >
                                    {isProcessing ? (
                                        <span>Processing...</span>
                                    ) : (
                                        <>
                                            <ApplePayIcon className="icon-large" />
                                            <span>Pay</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </ModalFooter>
                    </>
                )
            }
        </Modal>
    );
}

export default CartModal;